'use client';

import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { getPathnameWithoutLocale } from '@pioz/mlm-common';
import { NavigationHref } from '@pioz/mlm-common/types';

export interface UseActiveLinkProps {
  href: NavigationHref;
  locale: string;
  onlyExactHrefActive?: boolean;
  excludeFromActive?: NavigationHref[];
}

export function useActiveLinkState(props: UseActiveLinkProps) {
  const pathname = usePathname();

  return useMemo(
    () => isActiveLink({ ...props, pathname }),
    [pathname, props.href, props.locale, props.onlyExactHrefActive, props.excludeFromActive],
  );
}

export function isActiveLink({
  pathname,
  href,
  locale,
  onlyExactHrefActive,
  excludeFromActive,
}: UseActiveLinkProps & { pathname: string }) {
  const pathnameWithoutLocale = getPathnameWithoutLocale({ pathname, locales: [locale] });
  const hrefWithoutLocale = getPathnameWithoutLocale({ pathname: href, locales: [locale] });

  if (onlyExactHrefActive) {
    return pathnameWithoutLocale === hrefWithoutLocale;
  }

  if (
    excludeFromActive &&
    excludeFromActive.some((path) =>
      pathnameWithoutLocale.includes(
        getPathnameWithoutLocale({
          pathname: path,
          locales: [locale],
        }),
      ),
    )
  ) {
    return false;
  }

  return pathnameWithoutLocale.startsWith(hrefWithoutLocale);
}
